import { confirmSheet, showToast, unsavedLeaveSheet } from "../components/ui";
import type { DirtyGuard } from "./unsaved";
import {
  goAfterSave,
  goBack,
  navigateDocReturnOr,
} from "./navigation";
import { currentRouteTarget, type RouteResolverContext, type RouteTarget } from "../routes";
import { router } from "../router";

export type WireBackOpts = {
  panel: ParentNode;
  guard?: DirtyGuard | null;
  button?: string;
  onSave?: () => Promise<boolean>;
  onBack?: () => void;
  resolverContext?: RouteResolverContext;
};

export function wireBackButton(opts: WireBackOpts) {
  const btn = opts.panel.querySelector(opts.button || "#back-btn");
  if (!btn) return;
  btn.addEventListener("click", () => {
    const cur = currentRouteTarget(router.tab, router.sub, router.params);
    const go = () => {
      if (opts.onBack) opts.onBack();
      else goBack(cur, opts.resolverContext);
    };
    if (opts.guard) {
      void opts.guard.attemptLeave(go, { onSave: opts.onSave });
    } else {
      go();
    }
  });
}

export type WireHubBackOpts = {
  panel: ParentNode;
  button?: string;
  onBack?: () => void;
  resolverContext?: RouteResolverContext;
};

export function wireHubBackButton(opts: WireHubBackOpts) {
  const btn = opts.panel.querySelector(opts.button || "#back-btn");
  if (!btn) return;
  btn.addEventListener("click", () => {
    const cur = currentRouteTarget(router.tab, router.sub, router.params);
    if (opts.onBack) opts.onBack();
    else goBack(cur, opts.resolverContext);
  });
}

export type WireSaveOpts = {
  panel: ParentNode;
  guard?: DirtyGuard | null;
  button?: string;
  label?: string;
  savingLabel?: string;
  errorEl?: string;
  onSave: () => Promise<void>;
  afterSaveToast?: string;
  /** Called after a successful save. Defaults to goAfterSave when omitted. */
  onAfterSave?: () => void;
  /** When false, skip default post-save navigation (use when onSave navigates itself). */
  navigateAfterSave?: boolean;
  onSyncError?: (message: string) => void;
  resolverContext?: RouteResolverContext;
};

export function wireSaveButton(opts: WireSaveOpts) {
  const saveBtn = opts.panel.querySelector(opts.button || "#save-btn") as HTMLButtonElement | null;
  if (!saveBtn) return;
  const label = opts.label || saveBtn.textContent || "Save";
  const savingLabel = opts.savingLabel || "Saving…";
  const errEl = opts.errorEl
    ? (opts.panel.querySelector(opts.errorEl) as HTMLElement | null)
    : null;

  saveBtn.addEventListener("click", async () => {
    if (errEl) errEl.hidden = true;
    saveBtn.disabled = true;
    saveBtn.textContent = savingLabel;
    try {
      await opts.onSave();
      opts.guard?.clear();
      if (opts.afterSaveToast) showToast(opts.afterSaveToast, "success");
      if (opts.onAfterSave) opts.onAfterSave();
      else if (opts.navigateAfterSave !== false) {
        const cur = currentRouteTarget(router.tab, router.sub, router.params);
        goAfterSave(cur, opts.resolverContext);
      }
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : "Save failed.";
      if (errEl) {
        errEl.textContent = msg;
        errEl.hidden = false;
      } else {
        showToast(msg, "error");
      }
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = label;
    }
  });
}

export type WireFormScreenOpts = {
  panel: ParentNode;
  guard?: DirtyGuard | null;
  errorEl?: string;
  save?: Omit<WireSaveOpts, "panel">;
  back?: Omit<WireBackOpts, "panel">;
  resolverContext?: RouteResolverContext;
};

export function wireFormScreen(opts: WireFormScreenOpts) {
  if (opts.back) {
    wireBackButton({
      ...opts.back,
      panel: opts.panel,
      guard: opts.guard,
      onSave: opts.save
        ? async () => {
            try {
              await opts.save!.onSave();
              opts.guard?.clear();
              if (opts.save!.afterSaveToast) showToast(opts.save!.afterSaveToast, "success");
              if (opts.save!.onAfterSave) opts.save!.onAfterSave();
              else if (opts.save!.navigateAfterSave !== false) {
                const cur = currentRouteTarget(router.tab, router.sub, router.params);
                goAfterSave(cur, opts.resolverContext);
              }
              return true;
            } catch {
              return false;
            }
          }
        : undefined,
      resolverContext: opts.resolverContext,
    });
  }
  if (opts.save) {
    wireSaveButton({
      ...opts.save,
      panel: opts.panel,
      guard: opts.guard,
      errorEl: opts.errorEl,
      resolverContext: opts.resolverContext,
    });
  }
}

export type WireDeleteOpts = {
  panel: ParentNode;
  button: string;
  message: string;
  confirmLabel?: string;
  guard?: DirtyGuard | null;
  onDelete: () => Promise<void>;
  afterToast?: string;
  onAfter?: () => void;
  navigateTo?: RouteTarget;
};

export function wireDeleteAction(opts: WireDeleteOpts) {
  const btn = opts.panel.querySelector(opts.button);
  if (!btn) return;
  btn.addEventListener("click", async () => {
    const ok = await confirmSheet(opts.message, opts.confirmLabel || "Delete");
    if (!ok) return;
    try {
      await opts.onDelete();
      opts.guard?.clear();
      if (opts.afterToast) showToast(opts.afterToast, "success");
      if (opts.onAfter) opts.onAfter();
      else if (opts.navigateTo) {
        router.navigate(
          opts.navigateTo.tab,
          opts.navigateTo.sub,
          opts.navigateTo.params,
          { skipPush: true }
        );
      }
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Delete failed.", "error");
    }
  });
}

export type WireDocSuccessPanelOpts = {
  panel: ParentNode;
  onDone: () => void;
  onEdit: () => void;
  onDownload: () => void;
  onSend: () => void | Promise<void>;
  onCancelDoc?: () => void | Promise<void>;
};

export function wireDocSuccessPanel(opts: WireDocSuccessPanelOpts) {
  opts.panel.querySelector("#success-done")?.addEventListener("click", () => opts.onDone());
  opts.panel.querySelector("#success-edit")?.addEventListener("click", () => opts.onEdit());
  opts.panel.querySelector("#success-download")?.addEventListener("click", () => opts.onDownload());
  opts.panel.querySelector("#success-send")?.addEventListener("click", () => {
    void opts.onSend();
  });
  opts.panel.querySelector("#success-cancel-doc")?.addEventListener("click", () => {
    if (opts.onCancelDoc) void opts.onCancelDoc();
  });
}

export { navigateDocReturnOr };
